import React, { useState, useEffect } from 'react';
import { api } from '../../api.js';
import FileThumbPicker from './FileThumbPicker.jsx';
import RelatedLoadsTab from './RelatedLoadsTab.jsx';
import TrailerDocumentsTab from './TrailerDocumentsTab.jsx';

const blank = {
  unitNo: '', trailerType: '', length: '', vin: '', plate: '', year: '',
  dispatchStatus: 'Active', startDate: '', finishDate: '',
  ownership: '', isRetired: false,
  recurringPaymentEnabled: false, recurringPaymentAmount: '', recurringPaymentDay: ''
};

function fromTrailer(t) {
  if (!t) return blank;
  return {
    unitNo: t.unit_number || '', trailerType: t.trailer_type || '', length: t.length || '',
    vin: t.vin || '', plate: t.plate || '', year: t.year || '',
    dispatchStatus: t.dispatch_status || 'Active', startDate: t.start_date || '', finishDate: t.finish_date || '',
    ownership: t.ownership || '', isRetired: !!t.is_retired,
    recurringPaymentEnabled: !!t.recurring_payment_enabled,
    recurringPaymentAmount: t.recurring_payment_amount ?? '', recurringPaymentDay: t.recurring_payment_day ?? ''
  };
}

const TABS = [
  'Basic Information', 'Documents Info', 'Document Checklist', 'Trailer Related Documents',
  'Trailer Related Loads', 'Repairs', 'Recurring Payment'
];

export default function TrailerFormModal({ trailer, onClose, onSaved }) {
  const [savedId, setSavedId] = useState(trailer?.id || null);
  const [form, setForm] = useState(() => fromTrailer(trailer));
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [photo, setPhoto] = useState(trailer?.photo_url ? { url: trailer.photo_url, fileName: 'Photo' } : null);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // ── Document Checklist (tab 2) ──────────────────────────────────────────
  const [checklist, setChecklist] = useState(null);
  const loadChecklist = () => {
    if (!savedId) return;
    api(`/dispatch/trailers/${savedId}/checklist`).then(setChecklist).catch(() => setChecklist([]));
  };
  useEffect(() => { if (tab === 2 && savedId) loadChecklist(); }, [tab, savedId]);

  const toggleChecklistItem = async (item, isChecked) => {
    await api(`/dispatch/trailers/${savedId}/checklist/${item.doc_type_id}`, {
      method: 'POST', body: { isChecked, expiryDate: item.expiry_date }
    });
    loadChecklist();
  };
  const setChecklistExpiry = async (item, expiryDate) => {
    await api(`/dispatch/trailers/${savedId}/checklist/${item.doc_type_id}`, {
      method: 'POST', body: { isChecked: !!item.is_checked, expiryDate }
    });
    loadChecklist();
  };
  const addDocType = async () => {
    const name = window.prompt('New document type name');
    if (!name || !name.trim()) return;
    await api('/dispatch/trailer-doc-types', { method: 'POST', body: { name, isRequired: false } });
    loadChecklist();
  };

  // ── Trailer Related Documents (tab 3) — client-preview only
  const [relatedDocs, setRelatedDocs] = useState([]);
  const handleAddDocs = (e) => {
    const files = Array.from(e.target.files || []);
    setRelatedDocs(list => [...list, ...files.map(f => f.name)]);
  };

  // ── Repairs (tab 5) ──────────────────────────────────────────────────────
  const [repairs, setRepairs] = useState(null);
  const loadRepairs = () => {
    if (!savedId) return;
    api(`/dispatch/trailers/${savedId}/repairs`).then(setRepairs).catch(() => setRepairs([]));
  };
  useEffect(() => { if (tab === 5 && savedId) loadRepairs(); }, [tab, savedId]);
  const [repairForm, setRepairForm] = useState({ repairDate: '', description: '', cost: '', shop: '', mileage: '' });
  const addRepair = async () => {
    if (!repairForm.description.trim()) return;
    await api(`/dispatch/trailers/${savedId}/repairs`, { method: 'POST', body: repairForm });
    setRepairForm({ repairDate: '', description: '', cost: '', shop: '', mileage: '' });
    loadRepairs();
  };
  const deleteRepair = async (id) => {
    await api(`/dispatch/trailers/${savedId}/repairs/${id}`, { method: 'DELETE' });
    loadRepairs();
  };

  const persist = async () => {
    if (!form.unitNo.trim()) { setError('Unit No is required.'); return null; }
    setSaving(true);
    setError(null);
    try {
      const body = { ...form, photoUrl: photo?.url || '' };
      const saved = savedId
        ? await api(`/dispatch/trailers/${savedId}`, { method: 'PATCH', body })
        : await api('/dispatch/trailers', { method: 'POST', body });
      setSavedId(saved.id);
      return saved;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveAndClose = async () => { if (await persist()) onSaved(); };
  const saveAndNext = async () => {
    if (!(await persist())) return;
    setTab(t => Math.min(t + 1, TABS.length - 1));
  };
  const reset = () => { setForm(trailer ? fromTrailer(trailer) : blank); setPhoto(trailer?.photo_url ? { url: trailer.photo_url, fileName: 'Photo' } : null); };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal customer-modal" onClick={e => e.stopPropagation()}>
        <div className="customer-modal-title">{trailer ? 'Edit trailer' : 'New trailer'}</div>

        <div className="customer-modal-tabs">
          <div className="form-tabs" style={{ marginBottom: 0 }}>
            {TABS.map((t, i) => (
              <button key={t} type="button" className={i === tab ? 'form-tab active' : 'form-tab'} onClick={() => setTab(i)}>
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="customer-modal-body">
          {tab === 0 && (
            <>
              <label style={{ display: 'block', marginBottom: 18 }}>Photo<br/>
                <FileThumbPicker value={photo} onChange={setPhoto} label="Click to upload an image" />
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Unit No<br/><input value={form.unitNo} onChange={e => set('unitNo', e.target.value)} /></label>
                <label>Trailer Type<br/>
                  <select value={form.trailerType} onChange={e => set('trailerType', e.target.value)}>
                    <option value="">—</option>
                    <option>Dry Van</option><option>Flatbed</option><option>Reefer</option>
                    <option>Step Deck</option><option>Lowboy</option><option>Tanker</option>
                  </select>
                </label>
                <label>Length<br/><input value={form.length} onChange={e => set('length', e.target.value)} /></label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>VIN<br/><input value={form.vin} onChange={e => set('vin', e.target.value)} /></label>
                <label>Plate<br/><input value={form.plate} onChange={e => set('plate', e.target.value)} /></label>
                <label>Year<br/><input value={form.year} onChange={e => set('year', e.target.value)} /></label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Status (Active / Inactive)<br/>
                  <select value={form.dispatchStatus} onChange={e => set('dispatchStatus', e.target.value)}>
                    <option>Active</option><option>Inactive</option>
                  </select>
                </label>
                <label>Start Date<br/><input type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} /></label>
                <label>Finish Date<br/><input type="date" value={form.finishDate} onChange={e => set('finishDate', e.target.value)} /></label>
              </div>

              <label style={{ display: 'block', maxWidth: 320, marginBottom: 14 }}>Ownership<br/>
                <select value={form.ownership} onChange={e => set('ownership', e.target.value)}>
                  <option value="">—</option>
                  <option>Company owned</option>
                  <option>Owner operator</option>
                  <option>Leased</option>
                </select>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500 }}>
                <input type="checkbox" checked={form.isRetired} onChange={e => set('isRetired', e.target.checked)} style={{ width: 'auto' }} />
                Retired / Finished
              </label>
            </>
          )}

          {tab === 1 && (
            !savedId
              ? <div className="empty">Save the trailer first to log documents.</div>
              : <TrailerDocumentsTab trailerId={savedId} />
          )}

          {tab === 2 && (
            !savedId ? (
              <div className="empty">Save the trailer first to track document checklist items.</div>
            ) : checklist === null ? (
              <div className="empty">Loading...</div>
            ) : (
              <>
                <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 14 }}>
                  Check a document once it's in the system and set its expiry. Expired documents auto-uncheck and are flagged as required at dispatch. This list is specific to trailers.
                </div>
                {checklist.length === 0 && <div className="empty">No document types yet.</div>}
                {checklist.map(item => (
                  <div className="checklist-row" key={item.doc_type_id}>
                    <input type="checkbox" checked={!!item.is_checked} style={{ width: 'auto' }}
                      onChange={e => toggleChecklistItem(item, e.target.checked)} />
                    <div style={{ flex: 1 }}>{item.name}</div>
                    {!!item.is_required && <span className="checklist-req-badge">Required</span>}
                    <span style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 700 }}>EXPIRY</span>
                    <input type="date" value={item.expiry_date || ''} style={{ width: 150 }}
                      onChange={e => setChecklistExpiry(item, e.target.value)} />
                  </div>
                ))}
                <button type="button" onClick={addDocType} style={{ marginTop: 14 }}>+ Add document type</button>
              </>
            )
          )}

          {tab === 3 && (
            <>
              <input id="trailer-docs-input" type="file" multiple onChange={handleAddDocs} style={{ display: 'none' }} />
              <label htmlFor="trailer-docs-input" className="primary-btn" style={{ display: 'inline-block', marginBottom: 14, cursor: 'pointer' }}>
                Add New Documents +
              </label>
              {relatedDocs.length === 0 ? (
                <div className="empty">No documents uploaded yet.</div>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {relatedDocs.map((name, i) => <li key={i} style={{ fontSize: 13, marginBottom: 4 }}>{name}</li>)}
                </ul>
              )}
            </>
          )}

          {tab === 4 && (
            !savedId
              ? <div className="empty">No loads hauled by this trailer yet.</div>
              : <RelatedLoadsTab endpoint={`/dispatch/trailers/${savedId}/loads`} />
          )}

          {tab === 5 && (
            !savedId ? (
              <div className="empty">Save this trailer first, then repairs logged against it will show here.</div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '140px 2fr 120px 1fr 120px auto', gap: 10, marginBottom: 14 }}>
                  <input type="date" value={repairForm.repairDate} onChange={e => setRepairForm(f => ({ ...f, repairDate: e.target.value }))} />
                  <input placeholder="Description" value={repairForm.description} onChange={e => setRepairForm(f => ({ ...f, description: e.target.value }))} />
                  <input type="number" placeholder="Cost" value={repairForm.cost} onChange={e => setRepairForm(f => ({ ...f, cost: e.target.value }))} />
                  <input placeholder="Shop" value={repairForm.shop} onChange={e => setRepairForm(f => ({ ...f, shop: e.target.value }))} />
                  <input type="number" placeholder="Mileage" value={repairForm.mileage} onChange={e => setRepairForm(f => ({ ...f, mileage: e.target.value }))} />
                  <button type="button" className="primary-btn" onClick={addRepair}>+ Add</button>
                </div>
                {(repairs || []).length === 0 ? <div className="empty">No repairs logged yet.</div> : (
                  <table>
                    <thead><tr><th>Date</th><th>Description</th><th>Cost</th><th>Shop</th><th>Mileage</th><th></th></tr></thead>
                    <tbody>
                      {(repairs || []).map(r => (
                        <tr key={r.id}>
                          <td>{r.repair_date || '—'}</td>
                          <td>{r.description || '—'}</td>
                          <td>{r.cost != null ? Number(r.cost).toFixed(2) : '—'}</td>
                          <td>{r.shop || '—'}</td>
                          <td>{r.mileage ?? '—'}</td>
                          <td><button type="button" onClick={() => deleteRepair(r.id)} style={{ color: 'var(--red)' }}>Delete</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )
          )}

          {tab === 6 && (
            <>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 4px' }}
                onClick={() => set('recurringPaymentEnabled', !form.recurringPaymentEnabled)}>
                <span style={{
                  width: 40, height: 22, borderRadius: 999, background: form.recurringPaymentEnabled ? 'var(--blue)' : '#cbd0d9',
                  position: 'relative', transition: 'background .15s ease', flexShrink: 0
                }}>
                  <span style={{
                    position: 'absolute', top: 2, left: form.recurringPaymentEnabled ? 20 : 2,
                    width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .15s ease'
                  }} />
                </span>
                <span style={{ fontSize: 13, fontWeight: 500 }}>
                  Recurring payment {form.recurringPaymentEnabled ? 'ON' : 'OFF'} (monthly)
                </span>
              </label>

              {form.recurringPaymentEnabled && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 14, maxWidth: 400 }}>
                  <label>Amount<br/>
                    <input type="number" value={form.recurringPaymentAmount} onChange={e => set('recurringPaymentAmount', e.target.value)} />
                  </label>
                  <label>Day of month<br/>
                    <input type="number" min="1" max="31" value={form.recurringPaymentDay} onChange={e => set('recurringPaymentDay', e.target.value)} />
                  </label>
                </div>
              )}
            </>
          )}

          {error && <p style={{ color: '#c4364b', fontSize: 13, margin: '16px 0 0' }}>{error}</p>}
        </div>

        <div className="customer-modal-footer">
          <div className="modal-actions" style={{ margin: 0 }}>
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="button" onClick={reset}>Reset</button>
            <button type="button" className="primary-btn" onClick={saveAndNext} disabled={saving || tab === TABS.length - 1}>Save &amp; Next</button>
            <button type="button" className="primary-btn" onClick={saveAndClose} disabled={saving}>
              {saving ? 'Saving...' : trailer ? 'Save changes' : 'Create trailer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
